import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  addIctServiceAssessment,
  removeIctServiceAssessment,
  selectIctServiceAssessments,
  setIctServiceAssessments,
  updateIctServiceAssessment as updateIctServiceAssessmentState,
} from '../store/ictServiceAssessment/ictServiceAssessmentSlice.ts';
import {
  selectArrangements,
  setArrangements,
} from '../store/contractualArrangement/contractualArrangementSlice.ts';
import {
  selectDoraICTThirdPartyProviders,
  setDoraICTThirdPartyProviders,
} from '../store/ictThirdPartyProvider/ictThirdPartyProviderSlice.ts';
import {
  createDoraIctServiceAssessment,
  deleteDoraIctServiceAssessment,
  listDoraArrangements,
  listDoraIctServiceAssessments,
  listDoraICTThirdPartyProviders,
  updateDoraIctServiceAssessment,
} from '../../../api.ts';
import { DoraIctServiceAssessment } from '../models/doraIctServiceAssessment.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import { useExcelTableState } from '../../../hooks/useExcelTableState.ts';
import {
  parsePasteData,
  calculateNextCell,
  applyPastedData,
  getRowBackgroundColor,
  getRowHoverColor,
} from '../../../utils/excelTableUtils.ts';
import {
  ICT_SERVICE_TYPE_OPTIONS,
  SUBSTITUTABILITY_OPTIONS,
  NON_SUBSTITUTABILITY_REASON_OPTIONS,
  REINTEGRATION_POSSIBILITY_OPTIONS,
  DISCONTINUATION_IMPACT_OPTIONS,
  ALTERNATIVE_TPPS_IDENTIFIED_OPTIONS,
} from '../constants/ictServiceAssessment.ts';

interface EditableRow {
  id?: number;
  contractual_arrangement_id: number | '';
  ict_tpp_id: number | '';
  ict_service_type: string;
  substitutability: string;
  non_substitutability_reason: string;
  last_audit_date: string;
  has_exit_plan: boolean;
  reintegration_possibility: string;
  discontinuation_impact: string;
  has_alternative_tpps: string;
  alternative_tpp_details: string;
  isNew?: boolean;
  touched?: boolean;
}

export const ICTServiceAssessmentsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const assessments = useAppSelector(selectIctServiceAssessments);
  const contractualArrangements = useAppSelector(selectArrangements);
  const ictTpps = useAppSelector(selectDoraICTThirdPartyProviders);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editableRows, setEditableRows] = useState<EditableRow[]>([]);

  const {
    focusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus: baseHandleRowFocus,
    handleRowBlur: baseHandleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  } = useExcelTableState<EditableRow>();

  // Define field order for Tab navigation and paste
  const fieldOrder: (keyof EditableRow)[] = [
    'contractual_arrangement_id',
    'ict_tpp_id',
    'ict_service_type',
    'substitutability',
    'non_substitutability_reason',
    'last_audit_date',
    'has_exit_plan',
    'reintegration_possibility',
    'discontinuation_impact',
    'has_alternative_tpps',
    'alternative_tpp_details',
  ];

  // Convert assessments to editable rows and always add an empty row at the end
  const rows = useMemo(() => {
    const existingRows: EditableRow[] = assessments.map(a => ({
      id: a.id,
      contractual_arrangement_id: a.contractual_arrangement_id,
      ict_tpp_id: a.ict_tpp_id,
      ict_service_type: a.ict_service_type,
      substitutability: a.substitutability,
      non_substitutability_reason: a.non_substitutability_reason || '',
      last_audit_date: a.last_audit_date,
      has_exit_plan: a.has_exit_plan,
      reintegration_possibility: a.reintegration_possibility,
      discontinuation_impact: a.discontinuation_impact,
      has_alternative_tpps: a.has_alternative_tpps,
      alternative_tpp_details: a.alternative_tpp_details || '',
    }));

    // Add empty row for new entries
    const emptyRow: EditableRow = {
      contractual_arrangement_id: contractualArrangements[0]?.id ?? '',
      ict_tpp_id: ictTpps[0]?.id ?? '',
      ict_service_type: ICT_SERVICE_TYPE_OPTIONS[0],
      substitutability: SUBSTITUTABILITY_OPTIONS[0],
      non_substitutability_reason: '',
      last_audit_date: new Date().toISOString().split('T')[0],
      has_exit_plan: false,
      reintegration_possibility: REINTEGRATION_POSSIBILITY_OPTIONS[0],
      discontinuation_impact: DISCONTINUATION_IMPACT_OPTIONS[0],
      has_alternative_tpps: ALTERNATIVE_TPPS_IDENTIFIED_OPTIONS[4], // "No - assessment not yet performed"
      alternative_tpp_details: '',
      isNew: true,
    };

    return [...existingRows, emptyRow];
  }, [assessments, contractualArrangements, ictTpps]);

  useEffect(() => {
    setEditableRows(rows);
  }, [rows]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [currentOrganization]);

  const loadData = async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      setError(null);
      const [assessmentData, contractualArrangementData, ictTppData] = await Promise.all([
        listDoraIctServiceAssessments(),
        listDoraArrangements(),
        listDoraICTThirdPartyProviders(),
      ]);
      dispatch(setIctServiceAssessments(assessmentData));
      dispatch(setArrangements(contractualArrangementData));
      dispatch(setDoraICTThirdPartyProviders(ictTppData));
    } catch (err: any) {
      console.error('Failed to load ICT service assessments', err);
      setError(err?.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (index: number, field: keyof EditableRow, value: any) => {
    setEditableRows(prev => {
      const updated = [...prev];
      const row = updated[index];
      updated[index] = {
        ...row,
        [field]: value,
        ...(row.isNew && !row.touched ? { touched: true } : {}),
      };
      return updated;
    });
  };

  const saveRow = useCallback(async (row: EditableRow, index: number) => {
    const rowKey = row.id ?? `new-${index}`;

    if (isSaving(rowKey)) return;

    try {
      if (row.isNew) {
        // Create only if required fields filled
        if (row.contractual_arrangement_id && row.ict_tpp_id && row.ict_service_type.trim()) {
          markSaving(rowKey);
          const created = await createDoraIctServiceAssessment({
            contractual_arrangement_id: Number(row.contractual_arrangement_id),
            ict_tpp_id: Number(row.ict_tpp_id),
            ict_service_type: row.ict_service_type.trim(),
            substitutability: row.substitutability,
            non_substitutability_reason: row.non_substitutability_reason || null,
            last_audit_date: row.last_audit_date,
            has_exit_plan: row.has_exit_plan,
            reintegration_possibility: row.reintegration_possibility,
            discontinuation_impact: row.discontinuation_impact,
            has_alternative_tpps: row.has_alternative_tpps,
            alternative_tpp_details: row.alternative_tpp_details || null,
          });
          dispatch(addIctServiceAssessment(created));
        }
      } else if (row.id) {
        // Update existing
        if (row.contractual_arrangement_id && row.ict_tpp_id && row.ict_service_type.trim()) {
          markSaving(rowKey);
          const payload = {
            contractual_arrangement_id: Number(row.contractual_arrangement_id),
            ict_tpp_id: Number(row.ict_tpp_id),
            ict_service_type: row.ict_service_type.trim(),
            substitutability: row.substitutability,
            non_substitutability_reason: row.non_substitutability_reason || null,
            last_audit_date: row.last_audit_date,
            has_exit_plan: row.has_exit_plan,
            reintegration_possibility: row.reintegration_possibility,
            discontinuation_impact: row.discontinuation_impact,
            has_alternative_tpps: row.has_alternative_tpps,
            alternative_tpp_details: row.alternative_tpp_details || null,
          };
          const updated = await updateDoraIctServiceAssessment(row.id, payload);
          dispatch(updateIctServiceAssessmentState(updated));
        }
      }
    } catch (err: any) {
      console.error('Failed to save assessment', err);
      setError(err?.response?.data?.detail || 'Failed to save assessment');
    } finally {
      markSaved(rowKey);
    }
  }, [dispatch, isSaving, markSaving, markSaved]);

  const handleRowFocus = (index: number) => {
    baseHandleRowFocus(index, editableRows, saveRow);
  };

  const handleRowBlur = () => {
    baseHandleRowBlur(editableRows, saveRow);
  };

  const handleTabNavigation = (e: React.KeyboardEvent, currentRow: number, currentField: keyof EditableRow) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const nextCell = calculateNextCell(
      currentRow,
      currentFieldIndex,
      editableRows.length,
      fieldOrder.length,
      e.shiftKey
    );

    if (nextCell) {
      if (nextCell.row !== currentRow) {
        handleRowFocus(nextCell.row);
      }
      setEditingCell({ row: nextCell.row, field: fieldOrder[nextCell.fieldIndex] });
    }
  };

  const handlePaste = useCallback((e: React.ClipboardEvent, startRowIndex: number, startField: keyof EditableRow) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const startFieldIndex = fieldOrder.indexOf(startField);

    const parsedRows = parsePasteData(pasteData);

    // Normalize parsed data
    const normalizedRows = parsedRows.map(row =>
      row.map(cell => {
        const trimmed = cell.trim();
        if (trimmed === '—' || trimmed === '-' || trimmed === '–' || trimmed === '−' || trimmed === 'N/A' || trimmed === 'n/a') {
          return '';
        }
        return cell;
      })
    );

    const relatedEntitiesMap = new Map();
    relatedEntitiesMap.set('contractual_arrangement_id', contractualArrangements);
    relatedEntitiesMap.set('ict_tpp_id', ictTpps);

    const createEmptyRow = (): EditableRow => ({
      contractual_arrangement_id: contractualArrangements[0]?.id ?? '',
      ict_tpp_id: ictTpps[0]?.id ?? '',
      ict_service_type: ICT_SERVICE_TYPE_OPTIONS[0],
      substitutability: SUBSTITUTABILITY_OPTIONS[0],
      non_substitutability_reason: '',
      last_audit_date: new Date().toISOString().split('T')[0],
      has_exit_plan: false,
      reintegration_possibility: REINTEGRATION_POSSIBILITY_OPTIONS[0],
      discontinuation_impact: DISCONTINUATION_IMPACT_OPTIONS[0],
      has_alternative_tpps: ALTERNATIVE_TPPS_IDENTIFIED_OPTIONS[4],
      alternative_tpp_details: '',
      isNew: true,
      touched: true,
    });

    setEditableRows(prev => {
      const updated = applyPastedData(
        prev,
        normalizedRows,
        startRowIndex,
        startFieldIndex,
        fieldOrder,
        relatedEntitiesMap,
        createEmptyRow
      );
      return updated.map((row, idx) => {
        if (idx >= startRowIndex && idx < startRowIndex + normalizedRows.length && row.isNew) {
          return { ...row, touched: true };
        }
        return row;
      });
    });

    setTimeout(() => {
      normalizedRows.forEach((_, rowOffset) => {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex < editableRows.length) {
          saveRow(editableRows[targetRowIndex], targetRowIndex);
        }
      });
    }, 300);
  }, [editableRows, contractualArrangements, ictTpps, saveRow, fieldOrder]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;

    try {
      await deleteDoraIctServiceAssessment(id);
      dispatch(removeIctServiceAssessment(id));
    } catch (err: any) {
      console.error('Failed to delete assessment', err);
      setError(err?.response?.data?.detail || 'Failed to delete assessment');
    }
  };

  // Helper to determine if a cell should be highlighted as required
  const isRequiredFieldEmpty = (row: EditableRow, field: keyof EditableRow): boolean => {
    const requiredFields: (keyof EditableRow)[] = [
      'contractual_arrangement_id',
      'ict_tpp_id',
      'ict_service_type',
      'substitutability',
      'last_audit_date',
    ];
    if (!requiredFields.includes(field)) return false;

    const value = row[field];
    return value === '' || value === null || value === undefined;
  };

  const getCellBackgroundColor = (row: EditableRow, field: keyof EditableRow): string => {
    if (row.isNew && !row.touched) {
      return 'transparent';
    }

    if (isRequiredFieldEmpty(row, field)) {
      return 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 160, 0, 0.08))';
    }
    return 'transparent';
  };

  // Helper to format audit date for display
  const formatAuditDate = (date: string): string => {
    if (date === '9999-12-31') {
      return 'No audit performed';
    }
    return date;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Loading ICT Service Assessments...</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          B_07.01 - ICT Service Assessments
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Assessment of ICT services provided by third-party providers supporting critical or important functions
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {contractualArrangements.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Contractual Arrangements found. Please create contractual arrangements in the <strong>Contractual Arrangements</strong> panel (B_02.02) before creating assessments.
          </Alert>
        )}

        {ictTpps.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No ICT Third-Party Providers found. Please create ICT TPPs in the <strong>ICT Third-Party Providers</strong> panel (B_05.01) before creating assessments.
          </Alert>
        )}

        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 150 }}>
                  Contractual Arrangement
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 150 }}>
                  ICT TPP
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 150 }}>
                  ICT Service Type
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 130 }}>
                  Substitutability
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 150 }}>
                  Non-Subst. Reason
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 120 }}>
                  Last Audit Date
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 80 }}>
                  Exit Plan
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 120 }}>
                  Reintegration
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 120 }}>
                  Discontinuation Impact
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 150 }}>
                  Alt. TPPs Identified
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 150 }}>
                  Alt. TPP Details
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 60 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editableRows.map((row, index) => {
                const isFocused = focusedRowIndex === index;
                const isEvenRow = index % 2 === 0;
                const contractualArrangement = contractualArrangements.find(ca => ca.id === row.contractual_arrangement_id);
                const ictTpp = ictTpps.find(tpp => tpp.id === row.ict_tpp_id);

                // Show non-substitutability reason field only if needed
                const showNonSubstReason =
                  row.substitutability === 'Not substitutable' || row.substitutability === 'Highly complex substitutability';

                // Show alternative TPP details field only if needed
                const showAltTppDetails = row.has_alternative_tpps.startsWith('Yes');

                return (
                  <TableRow
                    key={row.id ?? `new-${index}`}
                    sx={{
                      bgcolor: getRowBackgroundColor(isFocused, row.isNew || false, isEvenRow),
                      '&:hover': {
                        bgcolor: getRowHoverColor(isFocused),
                      },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    {/* Contractual Arrangement */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'contractual_arrangement_id' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.contractual_arrangement_id === '' ? '' : String(row.contractual_arrangement_id)}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'contractual_arrangement_id', Number(e.target.value));
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'contractual_arrangement_id')}
                            displayEmpty
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {row.isNew && <MenuItem value=""><em>Select...</em></MenuItem>}
                            {contractualArrangements.map(ca => (
                              <MenuItem key={ca.id} value={ca.id}>{ca.reference_number}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'contractual_arrangement_id' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'pointer',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'contractual_arrangement_id'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {contractualArrangement?.reference_number || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                        </div>
                      )}
                    </TableCell>

                    {/* ICT TPP */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'ict_tpp_id' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.ict_tpp_id === '' ? '' : String(row.ict_tpp_id)}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'ict_tpp_id', Number(e.target.value));
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'ict_tpp_id')}
                            displayEmpty
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {row.isNew && <MenuItem value=""><em>Select...</em></MenuItem>}
                            {ictTpps.map(tpp => (
                              <MenuItem key={tpp.id} value={tpp.id}>{tpp.legal_name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'ict_tpp_id' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'pointer',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'ict_tpp_id'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {ictTpp?.legal_name || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                        </div>
                      )}
                    </TableCell>

                    {/* ICT Service Type */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'ict_service_type' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.ict_service_type}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'ict_service_type', e.target.value);
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'ict_service_type')}
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {ICT_SERVICE_TYPE_OPTIONS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'ict_service_type' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'pointer',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'ict_service_type'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.ict_service_type}
                        </div>
                      )}
                    </TableCell>

                    {/* Substitutability */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'substitutability' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.substitutability}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'substitutability', e.target.value);
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'substitutability')}
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {SUBSTITUTABILITY_OPTIONS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'substitutability' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'pointer',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'substitutability'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.substitutability}
                        </div>
                      )}
                    </TableCell>

                    {/* Non-Substitutability Reason (conditional) */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'non_substitutability_reason' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.non_substitutability_reason || ''}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'non_substitutability_reason', e.target.value);
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'non_substitutability_reason')}
                            displayEmpty
                            disabled={!showNonSubstReason}
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            <MenuItem value=""><em>N/A</em></MenuItem>
                            {NON_SUBSTITUTABILITY_REASON_OPTIONS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            if (showNonSubstReason) {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'non_substitutability_reason' });
                            }
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: showNonSubstReason ? 'pointer' : 'default',
                            userSelect: 'text',
                            background: showNonSubstReason && getCellBackgroundColor(row, 'non_substitutability_reason'),
                            transition: 'background 0.15s ease',
                            opacity: showNonSubstReason ? 1 : 0.5,
                          }}
                        >
                          {showNonSubstReason ? (row.non_substitutability_reason || <span style={{ color: '#999' }}>Select...</span>) : 'N/A'}
                        </div>
                      )}
                    </TableCell>

                    {/* Last Audit Date */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'last_audit_date' ? (
                        <TextField
                          type="date"
                          value={row.last_audit_date}
                          onChange={(e) => updateRow(index, 'last_audit_date', e.target.value)}
                          onBlur={() => {
                            handleRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleTabNavigation(e, index, 'last_audit_date')}
                          onPaste={(e) => handlePaste(e, index, 'last_audit_date')}
                          size="small"
                          fullWidth
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                          sx={{ '& input': { p: 1 } }}
                          autoFocus
                        />
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'last_audit_date' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'text',
                            userSelect: 'text',
                            background: getCellBackgroundColor(row, 'last_audit_date'),
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {formatAuditDate(row.last_audit_date)}
                        </div>
                      )}
                    </TableCell>

                    {/* Has Exit Plan (Checkbox) */}
                    <TableCell align="center">
                      <Checkbox
                        checked={row.has_exit_plan}
                        onChange={(e) => updateRow(index, 'has_exit_plan', e.target.checked)}
                        onClick={() => handleRowFocus(index)}
                        size="small"
                      />
                    </TableCell>

                    {/* Reintegration Possibility */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'reintegration_possibility' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.reintegration_possibility}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'reintegration_possibility', e.target.value);
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'reintegration_possibility')}
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {REINTEGRATION_POSSIBILITY_OPTIONS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'reintegration_possibility' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'pointer',
                            userSelect: 'text',
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.reintegration_possibility}
                        </div>
                      )}
                    </TableCell>

                    {/* Discontinuation Impact */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'discontinuation_impact' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.discontinuation_impact}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'discontinuation_impact', e.target.value);
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'discontinuation_impact')}
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {DISCONTINUATION_IMPACT_OPTIONS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'discontinuation_impact' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'pointer',
                            userSelect: 'text',
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.discontinuation_impact}
                        </div>
                      )}
                    </TableCell>

                    {/* Alternative TPPs Identified */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'has_alternative_tpps' ? (
                        <FormControl fullWidth size="small" variant="standard">
                          <Select
                            value={row.has_alternative_tpps}
                            onChange={(e: SelectChangeEvent<string>) => {
                              updateRow(index, 'has_alternative_tpps', e.target.value);
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'has_alternative_tpps')}
                            disableUnderline
                            sx={{ '& .MuiSelect-select': { p: 1 } }}
                            autoFocus
                          >
                            {ALTERNATIVE_TPPS_IDENTIFIED_OPTIONS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div
                          onClick={() => {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'has_alternative_tpps' });
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: 'pointer',
                            userSelect: 'text',
                            transition: 'background 0.15s ease',
                          }}
                        >
                          {row.has_alternative_tpps}
                        </div>
                      )}
                    </TableCell>

                    {/* Alternative TPP Details (conditional) */}
                    <TableCell>
                      {editingCell?.row === index && editingCell?.field === 'alternative_tpp_details' ? (
                        <TextField
                          value={row.alternative_tpp_details}
                          onChange={(e) => updateRow(index, 'alternative_tpp_details', e.target.value)}
                          onBlur={() => {
                            handleRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleTabNavigation(e, index, 'alternative_tpp_details')}
                          onPaste={(e) => handlePaste(e, index, 'alternative_tpp_details')}
                          size="small"
                          fullWidth
                          variant="standard"
                          placeholder={row.isNew ? (showAltTppDetails ? 'Enter details...' : 'N/A') : ''}
                          disabled={!showAltTppDetails}
                          InputProps={{ disableUnderline: true }}
                          sx={{ '& input': { p: 1 } }}
                          autoFocus
                        />
                      ) : (
                        <div
                          onClick={() => {
                            if (showAltTppDetails) {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'alternative_tpp_details' });
                            }
                          }}
                          style={{
                            padding: '8px',
                            minHeight: '20px',
                            cursor: showAltTppDetails ? 'text' : 'default',
                            userSelect: 'text',
                            transition: 'background 0.15s ease',
                            opacity: showAltTppDetails ? 1 : 0.5,
                          }}
                        >
                          {showAltTppDetails ? (row.alternative_tpp_details || (row.isNew ? <span style={{ color: '#999' }}>Enter details...</span> : '')) : 'N/A'}
                        </div>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      {!row.isNew && (
                        <IconButton size="small" onClick={() => handleDelete(row.id!)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Click cells to edit. Tab/Shift+Tab to navigate. Changes save automatically when moving to another row.
        </Typography>
      </CardContent>
    </Card>
  );
};
